import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/router";
import { Navbar } from "react-bootstrap";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  EnvironmentBadge,
  FixedToolbarItem,
  FlexToolbarItem,
  Toolbar,
  AvatarPopover,
  useToastManager,
  DarkModeToggle,
} from "@bagel-web/components";

import { WhoAmI } from "types/app";
import SwitchOrganization from "./SwitchOrganization";
import SwitchProject from "./SwitchProject";
import { postAPI } from "api";

const NavHeader = ({
  whoami,
  onChangeWhoAmI,
  onLogOut,
}: {
  whoami: WhoAmI;
  onChangeWhoAmI: (whoami: WhoAmI) => void;
  onLogOut: () => void;
}) => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { publishToast } = useToastManager();

  const handleSwitchOrganization = (organization_id: string) => {
    router.push(`/switch-organization/${organization_id}`);
  };

  const switchProjectMutation = useMutation({
    mutationFn: (project_id: string) =>
      postAPI<WhoAmI>("/api/auth/switch-project", { id: project_id }),
    onSuccess: (result) => {
      onChangeWhoAmI(result);
      // invalidate all of our caches
      queryClient.invalidateQueries({ queryKey: [] });
      router.push("/");
    },
    onError: () => {
      publishToast(`Error switching projects`);
    },
  });

  const isSwitchPending = switchProjectMutation.isPending;

  return (
    <Navbar className="navbar-dark bg-dark px-3" id="header">
      <Navbar.Brand>
        <Link href="/">
          <Image
            className="me-3"
            src="/static/aa_logo_90h_480_yellow.png"
            alt="Artificial Agency Logo"
            width={192}
            height={28}
            priority
            style={{ verticalAlign: "top" }}
          />
        </Link>
        <EnvironmentBadge />
      </Navbar.Brand>
      <Navbar.Collapse className="justify-content-end">
        <Toolbar>
          <FlexToolbarItem />

          <FixedToolbarItem>
            <Navbar.Text>
              <DarkModeToggle />
            </Navbar.Text>
          </FixedToolbarItem>

          <FixedToolbarItem>
            <Navbar.Text>
              <SwitchOrganization
                whoami={whoami}
                onSwitchOrganization={handleSwitchOrganization}
                disabled={isSwitchPending}
              />
            </Navbar.Text>
          </FixedToolbarItem>

          <FixedToolbarItem>
            <Navbar.Text>
              <div className="vr opacity-100" />
            </Navbar.Text>
          </FixedToolbarItem>

          <FixedToolbarItem>
            <Navbar.Text>
              <SwitchProject
                whoami={whoami}
                onSwitchProject={switchProjectMutation.mutate}
                disabled={isSwitchPending}
              />
            </Navbar.Text>
          </FixedToolbarItem>

          <AvatarPopover
            whoami={whoami}
            onLogOut={onLogOut}
            accountManagementHref="/account"
          />
        </Toolbar>
      </Navbar.Collapse>
    </Navbar>
  );
};

export default NavHeader;
